import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { hasModuleAccess, moduleForPath } from '@/lib/permissions'
import { Button } from '@/components/ui/Button'
import { useState, useEffect, useMemo } from 'react'
import {
  LayoutDashboard, Kanban, Users, UserCog, FileText, BarChart3, Settings, Megaphone,
  ChevronLeft, Newspaper, Share2, Lightbulb, Menu, Package, DollarSign, ChevronDown, Shield, ExternalLink,
  Send, Upload, GitBranch, TrendingUp, Search as SearchIcon, HeartHandshake, Building2, MailPlus, FolderOpen,
  Headphones, Ticket, HelpCircle, Mail, ClipboardCheck, ClipboardList } from "lucide-react"

interface NavChild {
  name: string
  href: string
  icon: any
  external?: boolean
}

interface NavItem {
  name: string
  href: string
  icon: any
  children?: NavChild[]
}

const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Pipeline', href: '/pipeline', icon: Kanban },
  { name: 'Leads', href: '/leads', icon: Users },
  { name: 'Propostas', href: '/proposals', icon: FileText },
  { name: 'Produtos', href: '/products', icon: Package },
  { name: 'Content Hub', href: '/content', icon: Newspaper },
  { name: 'Social', href: '/social', icon: Share2 },
  { name: 'Oportunidades', href: '/opportunities', icon: Lightbulb },
  { name: 'RDC', href: '/rdc', icon: ClipboardList },
  {
    name: 'Email Marketing',
    href: '/email-marketing',
    icon: MailPlus,
    children: [
      { name: 'Dashboard', href: '/email-marketing', icon: MailPlus },
      { name: 'Templates', href: '/email-marketing/templates', icon: MailPlus },
      { name: 'Listas', href: '/email-marketing/lists', icon: Users },
      { name: 'Contas', href: '/email-marketing/accounts', icon: Settings },
    ],
  },
  {
    name: 'Outreach',
    href: '/outreach',
    icon: Send,
    children: [
      { name: 'Dashboard', href: '/outreach', icon: Send },
      { name: 'Cadencias', href: '/outreach/cadences', icon: GitBranch },
      { name: 'Importar CSV', href: '/outreach/csv-import', icon: Upload },
    ]
  },
  {
    name: "Marketing Outbound",
    href: "/marketing",
    icon: TrendingUp,
    children: [
      { name: "Analise de Mercado", href: "/marketing/market-analysis", icon: SearchIcon },
      { name: "MQL Dashboard", href: "/marketing/mql-dashboard", icon: BarChart3 },
    ]
  },
  {
    name: "Customer Success",
    href: "/cs",
    icon: HeartHandshake,
    children: [
      { name: "Dashboard", href: "/cs", icon: BarChart3 },
      { name: "Clientes", href: "/cs/clients", icon: Building2 },
      { name: "Campanhas NPS", href: "/cs/campaigns", icon: Send },
    ]
  },
  { name: 'Campanhas', href: '/campaigns', icon: Megaphone },
  {
    name: 'Analytics',
    href: '/analytics',
    icon: BarChart3,
    children: [
      { name: 'Visao Geral', href: '/analytics', icon: BarChart3 },
      { name: 'DMARC', href: '/analytics/dmarc', icon: Shield },
    ]
  },
  { name: 'Sales Data Room', href: '/sales-dataroom', icon: FolderOpen },
  { name: 'Assessments', href: '/assessments-admin', icon: ClipboardCheck },
  {
    name: 'Nai Desk',
    href: '/nai-desk',
    icon: Headphones,
    children: [
      { name: 'Visao Geral', href: '/nai-desk', icon: LayoutDashboard },
      { name: 'Chamados', href: '/nai-desk/admin/tickets', icon: Ticket },
      { name: 'Melhorias', href: '/nai-desk/admin/melhorias', icon: Lightbulb },
      { name: 'FAQ', href: '/nai-desk/admin/faq', icon: HelpCircle },
      { name: 'Templates de Email', href: '/nai-desk/admin/email-templates', icon: Mail },
      { name: 'Configuracoes', href: '/nai-desk/admin/settings', icon: Settings },
    ],
  },
  { name: 'Usuarios', href: '/users', icon: UserCog },
  { name: 'Configuracoes', href: '/settings', icon: Settings },
]

// Find which parent menus should be expanded based on current path
function getDefaultExpanded(pathname: string): string[] {
  const expanded: string[] = []
  for (const item of navigation) {
    if (item.children) {
      if (pathname.startsWith(item.href)) {
        expanded.push(item.name)
      }
    }
  }
  // Always expand Analytics
  if (!expanded.includes('Analytics')) expanded.push('Analytics')
  if (!expanded.includes('Outreach') && pathname.startsWith('/outreach')) expanded.push('Outreach')
  if (!expanded.includes('Marketing Outbound') && pathname.startsWith('/marketing')) expanded.push('Marketing Outbound')
  if (!expanded.includes('Customer Success') && pathname.startsWith('/cs')) expanded.push('Customer Success')
  return expanded
}

export function Sidebar() {
  const location = useLocation()
  const { sidebarOpen, sidebarCollapsed, setSidebarOpen, setSidebarCollapsed } = useUIStore()
  const currentUser = useAuthStore((s) => s.user)
  const [expandedMenus, setExpandedMenus] = useState<string[]>(() => getDefaultExpanded(window.location.pathname))

  const visibleNavigation = useMemo(() => {
    return navigation
      .map((item) => {
        const itemModule = moduleForPath(item.href)
        const itemVisible = itemModule ? hasModuleAccess(currentUser, itemModule) : true
        if (!itemVisible) return null
        if (item.children) {
          const visibleChildren = item.children.filter((child) => {
            const childModule = moduleForPath(child.href)
            return childModule ? hasModuleAccess(currentUser, childModule) : true
          })
          return { ...item, children: visibleChildren }
        }
        return item
      })
      .filter((x): x is NavItem => x !== null)
  }, [currentUser])

  // Auto-expand parent when route changes
  useEffect(() => {
    setExpandedMenus(prev => {
      const needed = getDefaultExpanded(location.pathname)
      const merged = [...new Set([...prev, ...needed])]
      return merged
    })
  }, [location.pathname])

  const toggleExpand = (name: string) => {
    setExpandedMenus(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name])
  }

  return (
    <>
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      <Button variant="ghost" size="icon" className="fixed top-4 left-4 z-50 lg:hidden" onClick={() => setSidebarOpen(true)}>
        <Menu className="h-5 w-5" />
      </Button>

      <aside className={cn(
        'fixed inset-y-0 left-0 z-50 flex flex-col bg-card border-r transition-all duration-300',
        sidebarCollapsed ? 'w-16' : 'w-64',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
      )}>
        <div className="flex items-center justify-between h-16 px-4 border-b">
          {!sidebarCollapsed ? (
            <Link to="/" className="flex items-center justify-center flex-1">
              <img src="/Logo-nai-growth.webp" alt="Nai Growth" className="h-10 object-contain dark:hidden" />
              <img src="/Logo-nai-growth-branco.webp" alt="Nai Growth" className="h-10 object-contain hidden dark:block" />
            </Link>
          ) : (
            <Link to="/" className="flex items-center justify-center w-8 h-8">
              <img src="/Logo-nai-growth.webp" alt="Nai Growth" className="h-7 w-7 object-contain dark:hidden" />
              <img src="/Logo-nai-growth-branco.webp" alt="Nai Growth" className="h-7 w-7 object-contain hidden dark:block" />
            </Link>
          )}
          <Button variant="ghost" size="icon" className="hidden lg:flex" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
            <ChevronLeft className={cn('h-4 w-4 transition-transform', sidebarCollapsed && 'rotate-180')} />
          </Button>
        </div>

        <nav className="flex-1 overflow-y-auto p-2">
          <ul className="space-y-1">
            {visibleNavigation.map((item) => {
              const isActive = item.href === '/' ? location.pathname === '/' : location.pathname.startsWith(item.href)
              const isExpanded = expandedMenus.includes(item.name)
              const hasChildren = item.children && item.children.length > 0

              return (
                <li key={item.name}>
                  {hasChildren ? (
                    <>
                      <button
                        onClick={() => toggleExpand(item.name)}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors w-full text-left',
                          isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        )}
                      >
                        <item.icon className="h-5 w-5" flex-shrink-0 />
                        {!sidebarCollapsed && (
                          <>
                            <span className="flex-1">{item.name}</span>
                            <ChevronDown className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-180')} />
                          </>
                        )}
                      </button>
                      {isExpanded && !sidebarCollapsed && (
                        <ul className="ml-4" mt-1 space-y-1 border-l border-border pl-3>
                          {item.children!.map((child) => {
                            const childActive = child.href === '/analytics' ? location.pathname === '/analytics' : location.pathname.startsWith(child.href)
                            if (child.external) {
                              return (
                                <li key={child.name}>
                                  <a
                                    href={child.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                                  >
                                    <child.icon className="h-4 w-4 flex-shrink-0" />
                                    <span className="flex-1">{child.name}</span>
                                    <ExternalLink className="h-3 w-3 opacity-50" />
                                  </a>
                                </li>
                              )
                            }
                            return (
                              <li key={child.name}>
                                <Link
                                  to={child.href}
                                  className={cn(
                                    'flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                                    childActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                                  )}
                                  onClick={() => setSidebarOpen(false)}
                                >
                                  <child.icon className="h-4 w-4 flex-shrink-0" />
                                  <span>{child.name}</span>
                                </Link>
                              </li>
                            )
                          })}
                        </ul>
                      )}
                    </>
                  ) : (
                    <Link to={item.href} className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors',
                      isActive ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )} onClick={() => setSidebarOpen(false)}>
                      <item.icon className="h-5 w-5" flex-shrink-0 />
                      {!sidebarCollapsed && <span>{item.name}</span>}
                    </Link>
                  )}
                </li>
              )
            })}
          </ul>
        </nav>
      </aside>
    </>
  )
}
